import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { FileText, Plus, Trash2, FileSpreadsheet, Sparkles, AlertTriangle, Paperclip, Award, History, FileArchive, FileX2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '../lib/datetime';
import { MassBillingDialog } from '../components/MassBillingDialog';
import { ExternalSalesPanel } from '../components/ExternalSalesPanel';
import { SearchableSelect } from '../components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IVA_RATES = [
  { v: '21', label: '21%' },
  { v: '10.5', label: '10,5%' },
  { v: 'exento', label: 'Exento' },
  { v: 'no_alcanzado', label: 'No Alcanzado' },
];

const STATUS = {
  emitida: { label: 'Emitida', cls: 'bg-blue-100 text-blue-800' },
  parcial: { label: 'Parcial', cls: 'bg-amber-100 text-amber-800' },
  pagada: { label: 'Pagada', cls: 'bg-emerald-100 text-emerald-800' },
  anulada: { label: 'Anulada', cls: 'bg-slate-200 text-slate-700' },
  anulada_por_nc: { label: 'Anulada por NC', cls: 'bg-orange-100 text-orange-800' },
};

const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const q2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const rateNum = (r) => (r === '21' ? 0.21 : r === '10.5' ? 0.105 : 0);

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

const emptyLine = () => ({ service_id: null, description: '', quantity: 1, unit_price: 0, discount_pct: 0, iva_rate: '21' });
const emptyInvoice = () => ({
  target_kind: 'resident',  // v3.28.6 · 'resident' | 'insurer_direct'
  resident_id: '', insurer_id: '',
  document_type: 'FC', talonario: 'proforma',
  issue_date: new Date().toISOString().slice(0, 10), due_date: '',
  service_period_from: new Date().toISOString().slice(0, 10),
  service_period_to: new Date().toISOString().slice(0, 10),
  nc_source_invoice_id: '', notes: '', billed_to_target: 'resident', lines: [emptyLine()],
});

function Billing() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [residents, setResidents] = useState([]);
  const [services, setServices] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [filters, setFilters] = useState({ resident_id: '', document_type: '', status: '', insurer_id: '', search: '', date_from: '', date_to: '' });
  const [arcaCfg, setArcaCfg] = useState(null);
  const [dialog, setDialog] = useState({ open: false, data: emptyInvoice() });
  const [saving, setSaving] = useState(false);
  const [massBillingOpen, setMassBillingOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [residentSuggestions, setResidentSuggestions] = useState([]); // puntuales habituales del residente seleccionado

  const canEdit = userCan(user, 'residentes', 'editar');
  const [promote, setPromote] = useState({ open: false, invoice: null, saving: false });
  
  // v3.27.1 — Reporte de trazabilidad de pases a ARCA
  const _today = new Date();
  const _monthDefault = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, '0')}`;
  const [arcaHistory, setArcaHistory] = useState({ open: false, month: _monthDefault, rows: [], count: 0, total: 0, loading: false });
  
  const fetchArcaHistory = useCallback(async (month) => {
    setArcaHistory((h) => ({ ...h, loading: true }));
    try {
      const [y, m] = month.split('-').map(Number);
      const last = new Date(y, m, 0).getDate();
      const df = `${y}-${String(m).padStart(2, '0')}-01`;
      const dt = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
      const r = await axios.get(
        `${BACKEND_URL}/api/invoices/arca-promotions?date_from=${df}&date_to=${dt}`,
        { withCredentials: true }
      );
      setArcaHistory((h) => ({ ...h, rows: r.data?.rows || [], count: r.data?.count || 0, total: r.data?.total_amount || 0, loading: false }));
    } catch (e) {
      toast.error('Error al cargar el histórico');
      setArcaHistory((h) => ({ ...h, loading: false }));
    }
  }, []);
  
  const openArcaHistory = () => {
    setArcaHistory((h) => ({ ...h, open: true }));
    fetchArcaHistory(arcaHistory.month);
  };
  const canPromote = (inv) => (
    canEdit
    && inv?.talonario === 'proforma'
    && ['FC', 'ND'].includes(inv?.document_type)
    && !['anulada', 'anulada_por_nc'].includes(inv?.status)
    && !inv?.promoted_to_arca_at
    && (inv?.paid_amount || 0) === 0
    && (inv?.credited_amount || 0) === 0
    && !!arcaCfg?.certificate_configured
  );
  
  const openPromote = (inv) => setPromote({ open: true, invoice: inv, saving: false });
  const closePromote = () => setPromote({ open: false, invoice: null, saving: false });
  
  const confirmPromote = async () => {
    if (!promote.invoice) return;
    setPromote((p) => ({ ...p, saving: true }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/invoices/${promote.invoice.invoice_id}/promote-to-arca`,
        {}, { withCredentials: true }
      );
      const arca = r.data?.arca_invoice;
      toast.success(`Pasado a ARCA: ${arca?.invoice_number} · CAE ${arca?.cae || '—'}`);
      closePromote();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo pasar a ARCA');
      setPromote((p) => ({ ...p, saving: false }));
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const params = [];
      if (filters.resident_id) params.push(`resident_id=${filters.resident_id}`);
      if (filters.document_type) params.push(`document_type=${filters.document_type}`);
      if (filters.status) params.push(`status=${filters.status}`);
      if (filters.insurer_id) params.push(`insurer_id=${filters.insurer_id}`);
      if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
      if (filters.date_from) params.push(`date_from=${filters.date_from}`);
      if (filters.date_to) params.push(`date_to=${filters.date_to}`);
      const r = await axios.get(`${BACKEND_URL}/api/invoices?${params.join('&')}`, { withCredentials: true });
      setList(r.data || []);
    } catch (e) { toast.error('Error al cargar comprobantes'); }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/billing/arca-config`, { withCredentials: true })
      .then(res => setArcaCfg(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/residents`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/resident-services?include_inactive=false`, { withCredentials: true }),
      axios.get(`${BACKEND_URL}/api/health-insurers`, { withCredentials: true }),
    ]).then(([r, s, i]) => { setResidents(r.data || []); setServices(s.data || []); setInsurers(i.data || []); }).catch(() => {});
  }, []);

  const d = dialog.data;
  const needsSource = d.document_type === 'NC' || d.document_type === 'ND';
  const source = candidates.find(c => c.invoice_id === d.nc_source_invoice_id) || null;
  const residentName = (id) => residents.find(r => r.resident_id === id)?.full_name || '';

  const openNew = () => setDialog({ open: true, data: emptyInvoice() });

  const setF = (k, v) => setDialog(prev => ({ ...prev, data: { ...prev.data, [k]: v } }));

  const setLine = (i, k, v) => setDialog(prev => {
    const lines = [...prev.data.lines];
    lines[i] = { ...lines[i], [k]: v };
    return { ...prev, data: { ...prev.data, lines } };
  });
  const onPickService = (i, sid) => {
    const svc = services.find(s => s.service_id === sid);
    if (!svc) { setLine(i, 'service_id', null); return; }
    setDialog(prev => {
      const lines = [...prev.data.lines];
      lines[i] = { ...lines[i], service_id: sid, description: svc.name, unit_price: svc.current_price, iva_rate: svc.iva_rate };
      return { ...prev, data: { ...prev.data, lines } };
    });
  };
  const addLine = () => setF('lines', [...d.lines, emptyLine()]);
  const delLine = (i) => setF('lines', d.lines.filter((_, idx) => idx !== i));

  const loadAbonos = async () => {
    if (!d.resident_id) { toast.error('Elegí un residente primero'); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/residents/${d.resident_id}/abono-lines`, { withCredentials: true });
      if (!r.data || r.data.length === 0) { toast.info('El residente no tiene abonos activos'); return; }
      setF('lines', r.data.map(l => ({ ...emptyLine(), ...l })));
      toast.success(`${r.data.length} abono(s) cargado(s)`);
    } catch (e) { toast.error('Error al cargar abonos'); }
  };

  const onPickResident = async (rid) => {
    setF('resident_id', rid);
    setResidentSuggestions([]);
    setCandidates([]); setF('nc_source_invoice_id', '');
    const res = residents.find(r => r.resident_id === rid);
    const payer = (res?.responsibles || []).find(x => x.is_payment_responsible);
    const target = (payer && res?.billing_target_type === 'responsible') ? 'responsible' : 'resident';
    setF('billed_to_target', target);
    if (rid) {
      loadResidentSuggestions(rid);
      // v3.28.11 · incluir facturas del convenio si el destino es insurer
      if (needsSource) {
        const ins = target === 'insurer' ? (res?.billing_target_insurer_id || null) : null;
        loadCandidates(rid, ins);
      }
    }
  };

  const loadCandidates = async (rid, insurerId = null) => {
    try {
      // v3.28.11 · si la ND se factura al convenio, además de las FCs del residente
      // incluir las FCs del convenio (típicamente facturas preexistentes / legacy)
      const qs = new URLSearchParams();
      if (rid) qs.append('resident_id', rid);
      if (insurerId) qs.append('insurer_id', insurerId);
      const r = await axios.get(`${BACKEND_URL}/api/invoices/nc-candidates?${qs.toString()}`, { withCredentials: true });
      setCandidates(r.data || []);
      if (!r.data?.length) toast.info('No hay comprobantes de origen disponibles');
    } catch (e) { toast.error('Error al cargar los comprobantes de origen'); }
  };

  // v3.28.6 · Candidatos NC/ND para facturación directa al convenio.
  const loadInsurerCandidates = async (iid) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/invoices/nc-candidates?insurer_id=${iid}`, { withCredentials: true });
      setCandidates(r.data || []);
      if (!r.data?.length) toast.info('El convenio no tiene comprobantes de origen disponibles');
    } catch (e) { toast.error('Error al cargar los comprobantes del convenio'); }
  };

  const onPickType = (type) => {
    setDialog(prev => ({ ...prev, data: { ...prev.data, document_type: type, nc_source_invoice_id: '', nc_reason: '' } }));
    setCandidates([]);
    if (type !== 'FC') {
      if (d.target_kind === 'insurer_direct' && d.insurer_id) loadInsurerCandidates(d.insurer_id);
      else if (d.resident_id) {
        // v3.28.11 · si la ND se factura al convenio, incluir también los comprobantes del convenio
        const res = residents.find(r => r.resident_id === d.resident_id);
        const ins = d.billed_to_target === 'insurer' ? (res?.billing_target_insurer_id || null) : null;
        loadCandidates(d.resident_id, ins);
      }
    }
  };

  const onPickSource = (id) => {
    const src = candidates.find(c => c.invoice_id === id);
    setDialog(prev => ({
      ...prev,
      data: {
        ...prev.data,
        nc_source_invoice_id: id,
        // v3.28.12 · NO pisar el talonario. Una ND Proforma (interna, informativa en CC
        // del residente) puede referenciar una FC ARCA de origen; al hacer el pase
        // Proforma → ARCA, allí se aplica la regla fiscal. El talonario lo decide el
        // usuario en el selector, no lo forzamos por el comprobante de origen.
        lines: prev.data.document_type === 'NC' && src?.lines?.length
          ? src.lines.map(l => ({
              service_id: l.service_id || null, description: l.description || '',
              quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0,
              discount_pct: Number(l.discount_pct) || 0, iva_rate: l.iva_rate || '21',
            }))
          : prev.data.lines,
      },
    }));
  };
  const loadResidentSuggestions = async (rid) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/residents/${rid}/service-assignments`, { withCredentials: true });
      const active = (r.data || []).filter(a => a.is_active);
      // Para cada asignación, traer datos del servicio para distinguir abono vs puntual
      const withSvc = active.map(a => {
        const svc = services.find(s => s.service_id === a.service_id);
        return { ...a, _svc: svc };
      }).filter(a => a._svc);
      setResidentSuggestions(withSvc);
    } catch (e) { setResidentSuggestions([]); }
  };
  const addSuggestion = (a) => {
    const svc = a._svc;
    if (!svc) return;
    const price = a.price_override != null ? a.price_override : svc.current_price;
    const newLine = {
      service_id: svc.service_id, description: svc.name,
      quantity: a.quantity || 1, unit_price: price,
      discount_pct: a.discount_pct || 0, iva_rate: svc.iva_rate,
    };
    setF('lines', [...d.lines, newLine]);
  };

  const lineCalc = (l) => {
    const net = q2(q2(l.quantity * l.unit_price * (1 - (l.discount_pct || 0) / 100)));
    const iva = q2(net * rateNum(l.iva_rate));
    return { net, iva, total: q2(net + iva) };
  };
  const totals = d.lines.reduce((acc, l) => {
    const c = lineCalc(l);
    acc.net = q2(acc.net + c.net); acc.iva = q2(acc.iva + c.iva); acc.total = q2(acc.total + c.total);
    return acc;
  }, { net: 0, iva: 0, total: 0 });

  const save = async () => {
    const isInsurerDirect = d.target_kind === 'insurer_direct';
    if (!isInsurerDirect && !d.resident_id) { toast.error('Elegí un residente'); return; }
    if (isInsurerDirect && !d.insurer_id) { toast.error('Elegí un convenio'); return; }
    if (needsSource && !d.nc_source_invoice_id) { toast.error('Elegí el comprobante de origen'); return; }
    if (needsSource && !(d.nc_reason || '').trim()) { toast.error('Indicá el motivo'); return; }
    if (!d.lines.length || d.lines.some(l => !l.description || l.quantity <= 0)) { toast.error('Revisá los renglones'); return; }
    if (d.document_type === 'NC' && source && totals.total > q2(source.creditable) + 0.001) {
      toast.error(`El total supera el máximo acreditable (${fmt(source.creditable)})`); return;
    }
    setSaving(true);
    try {
      const payload = {
        resident_id: isInsurerDirect ? null : d.resident_id,
        insurer_id: isInsurerDirect ? d.insurer_id : null,
        document_type: d.document_type || 'FC',
        talonario: d.talonario,
        issue_date: d.issue_date,
        due_date: d.due_date || null,
        service_period_from: d.service_period_from || null,
        service_period_to: d.service_period_to || null,
        notes: d.notes || null,
        billed_to_target: isInsurerDirect ? 'insurer_direct' : (d.billed_to_target || 'resident'),
        nc_source_invoice_id: needsSource ? d.nc_source_invoice_id : null,
        nc_reason: needsSource ? (d.nc_reason || '').trim() : null,
        lines: d.lines.map(l => ({
          service_id: l.service_id || null, description: l.description,
          quantity: Number(l.quantity), unit_price: Number(l.unit_price),
          discount_pct: Number(l.discount_pct) || 0, iva_rate: l.iva_rate,
        })),
      };
      const r = await axios.post(`${BACKEND_URL}/api/invoices`, payload, { withCredentials: true });
      toast.success(`${r.data.invoice_number} emitido`);
      setDialog({ open: false, data: emptyInvoice() });
      setCandidates([]);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al emitir'); } finally { setSaving(false); }
  };

  const openPdf = (id) => window.open(`${BACKEND_URL}/api/invoices/${id}/pdf`, '_blank');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2"><FileText className="w-7 h-7 text-purple-500" /> Facturación</h1>
        <div className="flex gap-2">
          {canEdit && <Button variant="outline" onClick={() => navigate('/billing/batch-credit-note')} data-testid="batch-nc-btn" className="border-rose-300 text-rose-700 hover:bg-rose-50"><FileX2 className="w-4 h-4 mr-2" />NC Masiva</Button>}
          {canEdit && <Button variant="outline" onClick={() => setMassBillingOpen(true)} data-testid="mass-billing-btn" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"><Sparkles className="w-4 h-4 mr-2" />Facturación masiva</Button>}
          {canEdit && <ExternalSalesPanel embedded onCreated={fetchAll} />}
          <Button variant="outline" onClick={openArcaHistory} data-testid="arca-history-btn" className="border-violet-300 text-violet-700 hover:bg-violet-50"><History className="w-4 h-4 mr-2" />Pases a ARCA</Button>
          {canEdit && <Button onClick={openNew} data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-2" />Emitir comprobante</Button>}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex gap-3 items-end flex-wrap">
            <div><Label className="text-xs">Residente</Label>
              <select value={filters.resident_id} onChange={(e) => setFilters({ ...filters, resident_id: e.target.value })} className="flex h-10 w-56 rounded-md border px-2 text-sm" data-testid="filter-resident">
                <option value="">Todos</option>{residents.map(r => <option key={r.resident_id} value={r.resident_id}>{r.full_name}</option>)}
              </select></div>
            <div><Label className="text-xs">Tipo</Label>
              <select value={filters.document_type} onChange={(e) => setFilters({ ...filters, document_type: e.target.value })} className="flex h-10 w-32 rounded-md border px-2 text-sm">
                <option value="">Todos</option><option value="FC">Factura</option><option value="ND">Nota de Débito</option><option value="NC">Nota de Crédito</option>
              </select></div>
            <div><Label className="text-xs">Estado</Label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="flex h-10 w-36 rounded-md border px-2 text-sm">
                <option value="">Todos</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div><Label className="text-xs">Convenio</Label>
              <select value={filters.insurer_id} onChange={(e) => setFilters({ ...filters, insurer_id: e.target.value })} className="flex h-10 w-56 rounded-md border px-2 text-sm" data-testid="filter-insurer">
                <option value="">Todos</option>{insurers.map(i => <option key={i.insurer_id} value={i.insurer_id}>{i.name}</option>)}
              </select></div>
            <div className="flex-1 min-w-[220px]"><Label className="text-xs">Buscar comprobante</Label>
              <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="N° comprobante, residente, CAE…" className="h-10" data-testid="filter-search" />
            </div>
            <div><Label className="text-xs">Desde</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-10 w-40" data-testid="filter-date-from" />
            </div>
            <div><Label className="text-xs">Hasta</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-10 w-40" data-testid="filter-date-to" />
            </div>
            {(filters.date_from || filters.date_to) && (
              <div className="self-end">
                <Button variant="ghost" size="sm" onClick={() => setFilters({ ...filters, date_from: '', date_to: '' })} className="h-10 text-xs" data-testid="filter-date-clear">Limpiar fechas</Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Comprobante</TableHead><TableHead>Residente</TableHead><TableHead>Tipo</TableHead>
              <TableHead>Emisión</TableHead><TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Acreditado</TableHead><TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Sin comprobantes</TableCell></TableRow> :
                list.map(inv => {
                  const st = STATUS[inv.status] || STATUS.emitida;
                  return (
                    <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                      <TableCell className="font-mono text-xs font-medium">
                        {inv.invoice_number}
                        {inv.cae && (
                          <div className="text-[10px] text-emerald-700 font-normal mt-0.5" data-testid={`cae-${inv.invoice_id}`}>
                            CAE {inv.cae} · vto {fmtDate(inv.cae_due_date)}
                            {inv.arca_environment === 'homologacion' && ' · PRUEBA'}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{inv.resident_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{{ FC: 'Factura', NC: 'NC', ND: 'ND' }[inv.document_type] || inv.document_type}{inv.letra ? ` ${inv.letra}` : ''}</Badge>
                        {inv.document_type === 'NC' && inv.nc_source_number && <div className="text-[10px] text-muted-foreground mt-0.5">s/ {inv.nc_source_number}</div>}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(inv.issue_date)}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(inv.total)}</TableCell>
                      <TableCell className="text-right text-xs">{(inv.credited_amount || 0) > 0 ? fmt(inv.credited_amount) : '—'}</TableCell>
                      <TableCell>
                        <Badge className={st.cls} data-testid={`status-${inv.invoice_id}`}>{st.label}</Badge>
                        {inv.cancelled_by_nc_number && <div className="text-[10px] text-orange-700 mt-0.5">{inv.cancelled_by_nc_number}</div>}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" title="PDF" onClick={() => openPdf(inv.invoice_id)} data-testid={`pdf-${inv.invoice_id}`}><FileText className="w-4 h-4" /></Button>
                        {inv.talonario === 'arca' && inv.cae && inv.arca_txt_eligible && (
                          <Button variant="ghost" size="icon" title="Descargar TXTs ARCA (CABECERA/DETALLE/VENTAS para PAMI)"
                                  onClick={() => window.open(`${BACKEND_URL}/api/invoices/${inv.invoice_id}/arca-txt`, '_blank')}
                                  data-testid={`arca-txt-${inv.invoice_id}`}>
                            <FileArchive className="w-4 h-4 text-slate-700" />
                          </Button>
                        )}
                        {(inv.print_mode === 'summary' || (inv.lines || []).length > 1) && (
                          <Button variant="ghost" size="icon" title="Anexo: resumen mensual por afiliado"
                                  onClick={() => window.open(`${BACKEND_URL}/api/invoices/${inv.invoice_id}/annex-pdf`, '_blank')}
                                  data-testid={`annex-${inv.invoice_id}`}><Paperclip className="w-4 h-4 text-teal-600" /></Button>
                        )}
                        {canPromote(inv) && (
                          <Button variant="ghost" size="icon" title="Pasar a ARCA (revierte proforma y emite fiscal)"
                                  onClick={() => openPromote(inv)}
                                  data-testid={`promote-arca-${inv.invoice_id}`}>
                            <Award className="w-4 h-4 text-violet-600" />
                          </Button>
                        )}
                        {inv.replaced_by_invoice_number && (
                          <span className="text-[9px] text-violet-700 ml-1" title={`Pasada a ARCA: ${inv.replaced_by_invoice_number}`}>→ARCA</span>
                        )}
                        {inv.replaces_invoice_number && (
                          <span className="text-[9px] text-violet-700 ml-1" title={`Reemplaza a: ${inv.replaces_invoice_number}`}>ex-{inv.replaces_invoice_number}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, data: emptyInvoice() })}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Emitir {{ FC: 'factura', NC: 'Nota de Crédito', ND: 'Nota de Débito' }[d.document_type] || 'comprobante'} {d.talonario === 'arca' ? 'ARCA' : 'PROFORMA'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* v3.28.6 · Toggle destinatario: Residente vs Convenio directo */}
            <div className="inline-flex rounded-md border p-1 bg-muted/40 text-sm">
              <button type="button"
                onClick={() => setDialog({ open: true, data: { ...emptyInvoice(), document_type: d.document_type, talonario: d.talonario, issue_date: d.issue_date, target_kind: 'resident' } })}
                className={`px-3 py-1 rounded ${d.target_kind !== 'insurer_direct' ? 'bg-white shadow font-medium' : 'text-muted-foreground'}`}
                data-testid="target-kind-resident"
              >A un Residente</button>
              <button type="button"
                onClick={() => setDialog({ open: true, data: { ...emptyInvoice(), document_type: d.document_type, talonario: d.talonario, issue_date: d.issue_date, target_kind: 'insurer_direct' } })}
                className={`px-3 py-1 rounded ${d.target_kind === 'insurer_direct' ? 'bg-white shadow font-medium' : 'text-muted-foreground'}`}
                data-testid="target-kind-insurer"
              >A un Convenio</button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {d.target_kind === 'insurer_direct' ? (
                <div className="col-span-2"><Label>Convenio *</Label>
                  <SearchableSelect
                    value={d.insurer_id}
                    onChange={(val) => { setF('insurer_id', val); if (d.document_type !== 'FC') { /* NC/ND: cargar candidates del convenio */ loadInsurerCandidates(val); } }}
                    options={insurers.map(i => ({ value: i.insurer_id, label: i.name, hint: i.tax_id ? `CUIT ${i.tax_id}` : undefined }))}
                    placeholder="Buscar convenio..."
                    emptyLabel="Ningún convenio coincide"
                    testId="invoice-insurer"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    La deuda impacta en la cta.cte. del <b>convenio</b>. La cobranza posterior se aplica desagregando por residente.
                  </p>
                </div>
              ) : (
                <div className="col-span-2"><Label>Residente *</Label>
                  <SearchableSelect
                    value={d.resident_id}
                    onChange={onPickResident}
                    options={residents.map(r => ({ value: r.resident_id, label: r.full_name, hint: r.dni ? `DNI ${r.dni}` : undefined }))}
                    placeholder="Buscar por nombre o DNI..."
                    emptyLabel="Ningún residente coincide"
                    testId="invoice-resident"
                  /></div>
              )}
              <div><Label>Tipo *</Label>
                <select value={d.document_type} onChange={(e) => onPickType(e.target.value)}
                  className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="invoice-type">
                  <option value="FC">Factura</option>
                  <option value="NC">Nota de Crédito</option>
                  <option value="ND">Nota de Débito</option>
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {needsSource ? 'Requiere comprobante de origen' : 'Letra automática según el receptor'}
                </p></div>
              <div><Label>Talonario *</Label>
                <select value={d.talonario} onChange={(e) => setF('talonario', e.target.value)}
                  className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="invoice-talonario">
                  <option value="proforma">PROFORMA (interno)</option>
                  <option value="arca">ARCA (fiscal)</option>
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Letra automática según la condición IVA del receptor (A / B)
                </p></div>
              <div><Label>Emisión *</Label><Input type="date" value={d.issue_date} onChange={(e) => setF('issue_date', e.target.value)} /></div>
              {d.target_kind !== 'insurer_direct' && (
                <div className="col-span-2"><Label>¿A nombre de quién? *</Label>
                {(() => {
                  const res = residents.find(r => r.resident_id === d.resident_id);
                  const payer = (res?.responsibles || []).find(x => x.is_payment_responsible);
                  const os = insurers.find(o => o.insurer_id === res?.billing_target_insurer_id);
                  return (
                    <>
                      <select value={d.billed_to_target || 'resident'} onChange={(e) => {
                          const v = e.target.value;
                          setF('billed_to_target', v);
                          // v3.28.11 · Al cambiar el destinatario, recargar candidatos si es ND/NC
                          if (needsSource && d.resident_id) {
                            const ins = v === 'insurer' ? (res?.billing_target_insurer_id || null) : null;
                            setF('nc_source_invoice_id', '');
                            loadCandidates(d.resident_id, ins);
                          }
                        }}
                        className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="invoice-billed-to-target">
                        <option value="resident">Al residente{res ? ` — ${res.full_name}` : ''}</option>
                        {payer && <option value="responsible">Al responsable de pago — {payer.name}</option>}
                        {os && <option value="insurer">Al convenio — {os.name}</option>}
                      </select>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Define los datos fiscales y la letra del comprobante. La deuda impacta en la cuenta corriente del {d.billed_to_target === 'insurer' ? 'convenio' : 'residente'}.
                      </p>
                    </>
                  );
                })()}
                </div>
              )}
            </div>
            {needsSource && (
              <div className="rounded-md border border-orange-200 bg-orange-50/70 dark:bg-orange-950/20 dark:border-orange-900 p-3 space-y-3" data-testid="source-block">
                <p className="text-xs text-orange-900 dark:text-orange-200">
                  Toda {d.document_type === 'NC' ? 'Nota de Crédito' : 'Nota de Débito'} debe referenciar
                  obligatoriamente el comprobante de origen (criterio ARCA).
                  {d.document_type === 'NC' && ' La NC revierte el asiento, la cuenta corriente y el saldo del cliente.'}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label>Comprobante de origen *</Label>
                    <select value={d.nc_source_invoice_id} onChange={(e) => onPickSource(e.target.value)}
                      className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="invoice-source">
                      <option value="">{candidates.length ? 'Elegí el comprobante...' : 'Sin comprobantes disponibles'}</option>
                      {candidates.map(c => (
                        <option key={c.invoice_id} value={c.invoice_id}>
                          {c.invoice_number} · {fmtDate(c.issue_date)} · Total {fmt(c.total)}
                          {d.document_type === 'NC' ? ` · Acreditable ${fmt(c.creditable)}` : ''}
                        </option>
                      ))}
                    </select></div>
                  <div><Label>Motivo *</Label>
                    <Input value={d.nc_reason || ''} onChange={(e) => setF('nc_reason', e.target.value)}
                      placeholder={d.document_type === 'NC' ? 'Error de facturación' : 'Interés por mora'}
                      data-testid="invoice-reason" /></div>
                </div>
                {source && (
                  <p className="text-[11px] text-orange-800 dark:text-orange-300" data-testid="source-hint">
                    Talonario {source.talonario === 'arca' ? 'ARCA (fiscal)' : 'PROFORMA'} · letra {source.letra || '-'}
                    {d.document_type === 'NC' ? ` · máximo acreditable ${fmt(source.creditable)}` : ''}
                  </p>
                )}
              </div>
            )}
            {d.talonario === 'arca' && (
              arcaCfg?.certificate_configured ? (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 text-xs text-emerald-900 dark:text-emerald-200" data-testid="arca-ready-notice">
                  <span className="font-semibold">Talonario ARCA — emisión fiscal activa</span>
                  <span className="block mt-1">
                    Punto de venta <b>{arcaCfg?.punto_venta}</b> · ambiente <b>{arcaCfg?.environment === 'produccion' ? 'PRODUCCIÓN' : 'HOMOLOGACIÓN'}</b>.
                    Al emitir, el número y el CAE los otorga ARCA.
                    {arcaCfg?.environment === 'homologacion' && ' En homologación los comprobantes son de PRUEBA, sin validez fiscal.'}
                  </span>
                </div>
              ) : (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200" data-testid="arca-pending-notice">
                  <span className="font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Falta el certificado digital de ARCA
                  </span>
                  <span className="block mt-1">
                    Cargalo en Configuración → Facturación ARCA. Mientras tanto, emitilo como PROFORMA.
                  </span>
                </div>
              )
            )}
            {(
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Período Desde *</Label><Input type="date" value={d.service_period_from} onChange={(e) => setF('service_period_from', e.target.value)} data-testid="invoice-period-from" /></div>
                <div><Label>Período Hasta *</Label><Input type="date" value={d.service_period_to} onChange={(e) => setF('service_period_to', e.target.value)} data-testid="invoice-period-to" /></div>
                <div><Label>Vencimiento</Label><Input type="date" value={d.due_date} onChange={(e) => setF('due_date', e.target.value)} data-testid="invoice-due-date" /><p className="text-[11px] text-muted-foreground mt-1">Vacío = emisión + 7 días (auto)</p></div>
              </div>
            )}

            {d.resident_id && residentSuggestions.length > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-800 p-3" data-testid="resident-suggestions">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium">Servicios habituales del residente · agregar con un click</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {residentSuggestions.map((a) => (
                    <button
                      key={a.assignment_id}
                      type="button"
                      onClick={() => addSuggestion(a)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${a._svc.billing_type === 'abono' ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100' : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'}`}
                      data-testid={`suggestion-${a.assignment_id}`}
                      title={`${a._svc.billing_type.toUpperCase()} · ${fmt(a.price_override != null ? a.price_override : a._svc.current_price)}`}
                    >
                      <span className="font-semibold">{a._svc.name}</span>
                      <span className="ml-1 opacity-70">· {fmt(a.price_override != null ? a.price_override : a._svc.current_price)}</span>
                      <span className="ml-1 opacity-60 text-[10px]">({a._svc.billing_type})</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Los abonos los agregás más rápido con el botón &quot;Cargar abonos del residente&quot;. Los puntuales suelen ir línea por línea.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Renglones</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={loadAbonos} data-testid="load-abonos-btn"><Sparkles className="w-4 h-4 mr-1" />Cargar abonos del residente</Button>
                <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-4 h-4 mr-1" />Renglón</Button>
              </div>
            </div>

            <div className="space-y-2">
              {d.lines.map((l, i) => {
                const c = lineCalc(l);
                return (
                  <Card key={i}><CardContent className="pt-3 grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-2"><Label className="text-xs">Servicio (opcional)</Label>
                      <select value={l.service_id || ''} onChange={(e) => onPickService(i, e.target.value)} className="flex h-9 w-full rounded-md border px-2 text-xs">
                        <option value="">Manual</option>{services.map(s => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
                      </select></div>
                    <div className="col-span-5"><Label className="text-xs">Descripción</Label>
                      <Textarea className="min-h-[4.5rem] text-sm py-1.5 resize-y" rows={3}
                        placeholder="Detalle del concepto facturado (ej.: Adicional complementario — Beneficiario, Afiliado N°…)"
                        value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} data-testid={`line-desc-${i}`} />
                      <p className="text-[10px] text-muted-foreground mt-0.5">Admite varias líneas · ampliá con la esquina ↘</p>
                    </div>
                    <div className="col-span-1"><Label className="text-xs">Cant.</Label><Input className="h-9" type="number" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">P. Unit.</Label><MoneyInput className="h-9" value={l.unit_price} onChange={(e) => setLine(i, 'unit_price', e.target.value)} /></div>
                    <div className="col-span-1"><Label className="text-xs">Desc%</Label><Input className="h-9" type="number" value={l.discount_pct} onChange={(e) => setLine(i, 'discount_pct', e.target.value)} /></div>
                    <div className="col-span-1"><Label className="text-xs">IVA</Label>
                      <select value={l.iva_rate} onChange={(e) => setLine(i, 'iva_rate', e.target.value)} className="flex h-9 w-full rounded-md border px-1 text-xs">
                        {IVA_RATES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                      </select></div>
                    <div className="col-span-12 flex items-center justify-end gap-2 pt-1 border-t mt-1">
                      <span className="text-xs text-muted-foreground">Subtotal línea:</span>
                      <span className="text-sm font-semibold">{fmt(c.total)}</span>
                      {d.lines.length > 1 && <Button variant="ghost" size="icon" className="text-red-600 h-7 w-7" onClick={() => delLine(i)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </CardContent></Card>
                );
              })}
            </div>

            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal Neto</span><span>{fmt(totals.net)}</span></div>
                <div className="flex justify-between"><span>IVA</span><span>{fmt(totals.iva)}</span></div>
                <div className="flex justify-between font-bold text-base border-t pt-1"><span>TOTAL</span><span data-testid="invoice-total">{fmt(totals.total)}</span></div>
              </div>
            </div>

            <div><Label>Observaciones</Label><Textarea rows={2} value={d.notes} onChange={(e) => setF('notes', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: emptyInvoice() })}>Cancelar</Button>
            <Button onClick={save} disabled={saving || (d.talonario === 'arca' && !arcaCfg?.certificate_configured)} data-testid="save-invoice-btn">
              {saving ? (d.talonario === 'arca' ? 'Solicitando CAE a ARCA...' : 'Emitiendo...')
                : d.talonario === 'arca'
                  ? (arcaCfg?.certificate_configured ? 'Emitir con CAE' : 'Requiere certificado ARCA')
                  : 'Emitir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MassBillingDialog
        open={massBillingOpen}
        onOpenChange={setMassBillingOpen}
        onSuccess={fetchAll}
      />

      {/* v3.27.0 — Modal de confirmación "Pasar a ARCA" */}
      <Dialog open={promote.open} onOpenChange={(o) => !o && !promote.saving && closePromote()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-violet-600" />
              Pasar comprobante a ARCA
            </DialogTitle>
          </DialogHeader>
          {promote.invoice && (
            <div className="space-y-4 text-sm">
              <div className="bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 rounded-lg p-3 space-y-1">
                <p className="font-semibold text-violet-900 dark:text-violet-200">Vas a ejecutar 2 operaciones en cascada:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Emitir <b>NC PROFORMA</b> revirtiendo <b>{promote.invoice.invoice_number}</b> ({promote.invoice.document_type}
                    {promote.invoice.letra ? ` ${promote.invoice.letra}` : ''}, {fmtDate(promote.invoice.issue_date)}) por <b>{fmt(promote.invoice.total)}</b></li>
                  <li>Emitir un nuevo <b>{promote.invoice.document_type} ARCA</b> letra {promote.invoice.letra || 'B'} con las mismas líneas, período y monto — fecha <b>{fmtDate(new Date().toISOString().slice(0, 10))}</b> (hoy). Se solicita CAE a ARCA.</li>
                </ol>
              </div>
              <div className="bg-slate-50 dark:bg-slate-900 border rounded-lg p-3 text-xs space-y-1">
                <p><b>Cta.Cte. del residente</b> {promote.invoice.resident_name}: quedará visible solo el comprobante ARCA nuevo. La FC/ND proforma y la NC de reversión se ocultan de la CC (siguen visibles en el listado de comprobantes y en contabilidad para trazabilidad).</p>
                <p><b>Si ARCA rechaza el CAE:</b> la NC de reversión se anula automáticamente y el estado vuelve como estaba.</p>
              </div>
              <div className="text-xs text-muted-foreground">
                Total involucrado: <b>{fmt(promote.invoice.total)}</b> · Período: {fmtDate(promote.invoice.service_period_from)} — {fmtDate(promote.invoice.service_period_to)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePromote} disabled={promote.saving}>Cancelar</Button>
            <Button onClick={confirmPromote} disabled={promote.saving} className="bg-violet-600 hover:bg-violet-700"
                    data-testid="confirm-promote-arca-btn">
              {promote.saving ? 'Solicitando CAE a ARCA...' : 'Confirmar pase a ARCA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.27.1 — Histórico de Pases a ARCA */}
      <Dialog open={arcaHistory.open} onOpenChange={(o) => setArcaHistory((h) => ({ ...h, open: o }))}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-violet-600" />
              Pases a ARCA — Reporte de trazabilidad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-xs">Período (mes calendario)</Label>
                <Input type="month" value={arcaHistory.month}
                       onChange={(e) => {
                         const m = e.target.value || _monthDefault;
                         setArcaHistory((h) => ({ ...h, month: m }));
                         fetchArcaHistory(m);
                       }}
                       data-testid="arca-history-month" className="w-40" />
              </div>
              <div className="ml-auto text-right text-sm">
                <div className="text-muted-foreground">Pases del mes</div>
                <div className="font-bold text-lg text-violet-700" data-testid="arca-history-count">
                  {arcaHistory.count} · {fmt(arcaHistory.total)}
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha del pase</TableHead>
                  <TableHead>Residente</TableHead>
                  <TableHead>Original (proforma)</TableHead>
                  <TableHead>NC de reversión</TableHead>
                  <TableHead>ARCA generada</TableHead>
                  <TableHead>CAE</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Usuario</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {arcaHistory.loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Cargando...</TableCell></TableRow>
                ) : arcaHistory.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Sin pases a ARCA en este mes</TableCell></TableRow>
                ) : arcaHistory.rows.map((r) => (
                  <TableRow key={r.original_invoice_id} data-testid={`arca-history-row-${r.original_invoice_id}`}>
                    <TableCell className="text-xs">{fmtDate(r.promoted_at)}</TableCell>
                    <TableCell className="text-sm">{r.resident_name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <div>{r.original_number}</div>
                      <div className="text-[10px] text-muted-foreground">{r.original_document_type} · {fmtDate(r.original_issue_date)}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-amber-700">
                      <div>{r.nc_number || '—'}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtDate(r.nc_issue_date)}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-emerald-700 font-medium">
                      <div>{r.arca_number || '—'}</div>
                      <div className="text-[10px] text-muted-foreground">{r.arca_document_type} {r.arca_letra} · {fmtDate(r.arca_issue_date)}</div>
                    </TableCell>
                    <TableCell className="text-[10px] font-mono">
                      {r.arca_cae ? (
                        <div>
                          <div className="font-medium">{r.arca_cae}</div>
                          <div className="text-muted-foreground">vto {fmtDate(r.arca_cae_due_date)}</div>
                          {r.arca_environment === 'homologacion' && <div className="text-amber-700">PRUEBA</div>}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium text-sm">{fmt(r.total)}</TableCell>
                    <TableCell className="text-xs">{r.promoted_by || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArcaHistory((h) => ({ ...h, open: false }))}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default Billing;
